import type { ApplicationDraft } from "../../api/client";
import { Callout } from "../../components/Callout";
import { TextField } from "../../components/TextField";

/**
 * Screen 05 · Key people and roles.
 *
 * The certification decision maker is held separately from the lead auditor on purpose:
 * Cl. 9.5 requires the certification decision to be made by someone who did not carry out
 * the audit. Complao records both roles so that the separation is evidenced even though
 * the decision itself is taken outside the platform — which is why this step names them
 * and the *allocation* gate is what enforces the separation.
 *
 * Every email uses `type="text"` with `inputmode="email"`. `type="email"` returns null
 * from `selectionStart`, so the caret cannot be restored on re-render and the address
 * types backwards — data dictionary §9, a real defect rather than a hypothetical.
 */
export function PeopleStep({
  draft,
  set,
  errorFor,
}: {
  draft: ApplicationDraft;
  set: (patch: Partial<ApplicationDraft>) => void;
  errorFor: (field: string) => string | undefined;
}) {
  return (
    <section className="card">
      <h2>Key people and roles</h2>

      <Callout
        tone="warn"
        title="The certification decision maker is recorded separately from the lead auditor on purpose."
      >
        Cl. 9.5 requires the certification decision to be made by someone who did not carry out
        the audit. Complao holds both roles so that separation is evidenced even though the
        decision itself happens outside the platform.
      </Callout>

      <div className="grid2">
        <TextField
          id="admin_contact"
          label="Administrative contact"
          hint="Configures audits in Complao"
          value={draft.admin_contact ?? ""}
          onChange={(value) => set({ admin_contact: value })}
          error={errorFor("admin_contact")}
          required
        />
        <TextField
          id="admin_email"
          label="Administrative contact email"
          kind="email"
          value={draft.admin_email ?? ""}
          onChange={(value) => set({ admin_email: value })}
          error={errorFor("admin_email")}
          required
        />
      </div>

      <div className="grid2">
        <TextField
          id="decision_maker"
          label="Certification decision maker"
          hint="Cl. 9.5 — must not audit what they decide"
          value={draft.decision_maker ?? ""}
          onChange={(value) => set({ decision_maker: value })}
          error={errorFor("decision_maker")}
          required
        />
        <TextField
          id="decision_maker_email"
          label="Decision maker email"
          kind="email"
          value={draft.decision_maker_email ?? ""}
          onChange={(value) => set({ decision_maker_email: value })}
          error={errorFor("decision_maker_email")}
        />
      </div>

      <div className="grid2">
        <TextField
          id="technical_manager"
          label="Technical manager"
          hint="Approves auditor competence records"
          value={draft.technical_manager ?? ""}
          onChange={(value) => set({ technical_manager: value })}
          error={errorFor("technical_manager")}
          required
        />
        <TextField
          id="billing_contact"
          label="Billing contact"
          kind="email"
          value={draft.billing_contact ?? ""}
          onChange={(value) => set({ billing_contact: value })}
          error={errorFor("billing_contact")}
        />
      </div>
    </section>
  );
}
